import { Map } from 'immutable'
import {
  cond,
  F,
  T,
  either,
  equals,
  not,
  gt,
  lt,
  gte,
  lte,
  or,
  and,
  curry,
  head,
  __,
  compose,
  map,
  values,
  flatten,
  isNil,
  prop,
} from 'ramda'
import {
  get,
  getEq,
  getIn,
  getOr,
  getInEq,
  getInOr,
  pickToJS,
  pick,
  pickIn,
  has,
  safeToJS,
  where,
} from 'utils/immutable'
import {
  CARRY,
  TOGGLE,
  NATIVE_INPUT,
  CAN_INPUT,
  TIMER,
  COMPLEX,
  M0,
  M1,
  NO_EDGE,
  RISING_EDGE,
  FALLING_EDGE,
  BOTH_EDGES,
} from 'utils/constants'
import { strictAnyResult } from 'utils/functional'
import {
  POWER_NET_LEVELS_MAPPING,
  POWER_NET_LEVEL_SIGNAL_URIS,
  BATTERY_VOLTAGE_RANGE,
} from 'constants/featureOutputs'

export const getRefQueryUri = get('refQueryUri')
export const getParentQueryUri = get('parentQueryUri')
export const getSubType = get('subType')
export const getQueryUri = get('uri')
export const getInitialValue = get('initialValue')
export const getTimerMode = get('timerMode')
export const getCanId = (state) => getCanIdFromCondition(state.toJS())
export const getSignalUri = get('signal')
export const getDependencyKey = either(getSignalUri, getCanId)

export const isEmptyCondition = where({
  subType: equals(NATIVE_INPUT),
  signal: isNil,
})

// simulated can id is a combination of the canId and startBit to differentiate between messages
export const getCanIdFromCondition = ({
  canId,
  startBit = 0,
  numberOfBits = 0,
}) => {
  if (!canId) return null
  return `${canId}-${startBit}-${numberOfBits}`
}

export const conditionReferencesParent = (condition) =>
  has('parentQueryUri', condition) &&
  has('refQueryUri') &&
  equals(getParentQueryUri(condition), getRefQueryUri(condition))

export const referencedEntityIsTimer = (uri, collection) =>
  getInEq([uri, 'queryType'], TIMER, collection)

export const shapeValue = (val, isTimer) =>
  Map({ [isTimer ? 'initialValue' : 'default']: val, current: val })

export const strictAnyDefaultValue = (uri, signalsMap) =>
  strictAnyResult([
    getIn([uri, 'interface', 'defaultValueOverride']),
    getIn([uri, 'defaultValue']),
    getIn([uri, 'translation', 'defaultValue']),
  ])(signalsMap)

export const makeSingleMap = (key, value, isTimer = false) =>
  Map({ [key]: shapeValue(value, isTimer) })

export const makeEvaluationSingleMap = (
  key,
  evaluation,
  previousBaseResult,
  edgeEvaluation
) =>
  Map({
    [key]: Map({
      evaluation,
      ...(previousBaseResult !== undefined && { previousBaseResult }),
      ...(edgeEvaluation !== undefined && { edgeEvaluation }),
    }),
  })

export const makeOutputtingSingleMap = (key, outputting) =>
  Map({
    [key]: Map({
      outputting,
    }),
  })

export const deriveDefaultConditionSimulationValue = curry(
  (immQueriesMap, immSignalsMap, immCondition) => {
    const subType = getSubType(immCondition)
    const signalUri = getSignalUri(immCondition)
    if (isEmptyCondition(immCondition)) {
      // condition hasn't been defined
      return null
    }
    if (subType === CARRY) {
      const refQueryUri = getRefQueryUri(immCondition)
      const isTimer = referencedEntityIsTimer(refQueryUri, immQueriesMap)
      if (isTimer) {
        // evaluate against timer value
        return makeSingleMap(
          refQueryUri,
          getIn([refQueryUri, 'initialValue'], immQueriesMap),
          true
        )
      }
      return null // carries from logic sets don't have a default value
    }
    if (subType === CAN_INPUT) {
      return makeSingleMap(getCanId(immCondition), 0)
    }

    if (Object.values(POWER_NET_LEVEL_SIGNAL_URIS).includes(signalUri)) {
      // power net level signals have custom simulation defaults
      return null
    }
    return makeSingleMap(
      signalUri,
      strictAnyDefaultValue(signalUri, immSignalsMap)
    )
  }
)

const gateOpFns = {
  and,
  or,
  xor: (a, b) => a !== b,
}

export const getGateOpFnAndCompare = ({
  op = TOGGLE,
  upstreamSimEvals,
  previousGateSim,
  previousEnableSim,
}) => {
  const [aVal, bVal] = upstreamSimEvals
  const aIsEmpty = aVal === null || aVal === undefined
  const bIsEmpty = bVal === null || bVal === undefined

  // If both inputs are empty (no conditions configured for this gate),
  // return null (neutral) so downstream gates treat this as a single-input passthrough.
  // Display layer shows null as red during simulation.
  if (aIsEmpty && bIsEmpty) return null

  if (op === TOGGLE) {
    // Treat null (empty slot) as false: no reset and no enable signal.
    const effectiveBVal = bIsEmpty ? false : bVal
    const effectiveAVal = aIsEmpty ? false : aVal
    if (effectiveBVal) {
      // bVal is the 'reset' so if bVal, always return false
      return false
    }
    // TODO-Joe: confirm that the toggle gate _must_ receive a rising edge from its
    // enable condition in order to turn on
    if (effectiveAVal && !previousEnableSim) {
      // if enable value has just turned on, flip toggle
      return !previousGateSim
    }
    return previousGateSim // otherwise, maintain toggle
  }

  // AND requires both inputs to be present — a missing slot is not neutral.
  if (op === 'and' && (aIsEmpty || bIsEmpty)) return null

  // Single-input gate: empty slot is neutral — pass through the real input.
  if (aIsEmpty) return bVal
  if (bIsEmpty) return aVal

  return gateOpFns[op](aVal, bVal)
}

const FINAL = 'final'

const gates = ['ab', 'cd', FINAL]
const opSuffix = 'ComplexQueryOperator'
const simSuffix = 'GateSimulation'

const gateDependencyKeys = {
  timer: [0, 1],
  ab: [0, 1],
  cd: [2, 3],
  final: ['abGateSimulation', 'cdGateSimulation'],
}

export const getUpstreamEntitySimulatedValue = (
  previousSimulations,
  inProgressSimulatedEvaluations,
  key,
  selfReference
) => {
  // first check for carries
  const initial = get(key, inProgressSimulatedEvaluations)
  if (!selfReference && initial) {
    return getSimulatedValue(initial)
  }
  // simple conditions and carries that haven't been evaluated yet
  const fallback = get(key, previousSimulations)
  if (fallback) {
    return getSimulatedValue(fallback)
  }

  // failure case
  return false
}

export const getPreviousEnableSimulation = curry(
  (previousSimulations, query, gatePrefix) => {
    const getterFn = getInOr(false, __, previousSimulations)
    if (gatePrefix === FINAL) {
      return getterFn([query.get('uri'), 'abGateSimulation'])
    }
    const conditionUri = getIn(
      ['conditions', head(gateDependencyKeys[gatePrefix])],
      query
    )
    return getterFn([conditionUri, 'evaluation'])
  }
)

const conditionOpFns = {
  2: equals,
  3: (a, b) => not(equals(a, b)),
  4: gt,
  5: lt,
  6: gte,
  7: lte,
}

export const deriveEvaluationResult = ({
  subType,
  result,
  simulatedValue,
  value,
  operator,
  timerCarry,
}) => {
  const operatingValue = subType === CARRY && !timerCarry ? result : value
  return conditionOpFns[operator](simulatedValue, operatingValue)
}

export const applyEdgeDetection = (baseResult, previousBaseResult, edge) => {
  if (!edge || edge === NO_EDGE) return baseResult
  if (edge === RISING_EDGE) return baseResult && !previousBaseResult
  if (edge === FALLING_EDGE) return !baseResult && previousBaseResult
  if (edge === BOTH_EDGES) return baseResult !== previousBaseResult
  return baseResult
}

export const getSimulatedValue = cond([
  [
    // an open virtual fuse can short circuit conditions to false
    getEq('fuseOpen', true),
    F,
  ],
  [
    // a carry references a logic set or timer, which has the 'outputtting' property
    // if that property is set to 'false', then that block is not outputting anything
    getEq(false, 'outputting'),
    F,
  ],
  [
    has('finalGateSimulation'),
    get('outputting'), // carrying a logic set will grab this value
  ],
  [has('evaluation'), get('evaluation')], // acount for single queries that feed into outputs
  [
    T,
    getOr(false, 'current'), // timers and other input simulations have a 'current' property
  ],
])

export const handleConditionQueryEvaluation = (
  currentSimulatedValues,
  allQueriesMap,
  inProgressSimulatedEvaluations,
  query
) => {
  const uri = getQueryUri(query)
  const refQueryUri = getRefQueryUri(query)
  const canId = getCanId(query)
  const signalUri = getSignalUri(query)

  const baseConditionProps = pickToJS(
    ['result', 'value', 'operator', 'subType', 'edge'],
    query
  )

  const simulatedValue = getUpstreamEntitySimulatedValue(
    currentSimulatedValues,
    inProgressSimulatedEvaluations,
    refQueryUri || canId || signalUri,
    // we only have a `refQueryUri` if our query is a carry of some sort
    // if our `refQueryUri` is the same as our parent, we must reference the previous
    // simulated evaluation
    conditionReferencesParent(query)
  )

  const timerCarry =
    refQueryUri && referencedEntityIsTimer(refQueryUri, allQueriesMap)

  const baseResult = deriveEvaluationResult({
    ...baseConditionProps,
    simulatedValue,
    timerCarry,
  })

  const { edge } = baseConditionProps
  const previousBaseResult = getInOr(
    false,
    [uri, 'previousBaseResult'],
    currentSimulatedValues
  )
  const edgeResult = applyEdgeDetection(baseResult, previousBaseResult, edge)
  // For NO_EDGE, use baseResult as edgeEvaluation (passthrough behavior)
  // This ensures the evaluation icon is visible regardless of edge trigger setting
  const edgeEvaluation = !edge
    ? null
    : edge === NO_EDGE
    ? baseResult
    : edgeResult

  return makeEvaluationSingleMap(uri, edgeResult, baseResult, edgeEvaluation)
}

export const handleActivateValueUpdate = (
  previousSimulatedValue,
  initialValue,
  timerMode
) => {
  if (timerMode === M0) {
    return previousSimulatedValue === 0
      ? initialValue
      : previousSimulatedValue - 1
  }
  return previousSimulatedValue === 0 ? 0 : previousSimulatedValue - 1
}

export const handleNonActivateNonResetValueUpdate = (
  previousSimulatedValue,
  initialValue,
  timerMode
) => {
  if (timerMode === M1) {
    return previousSimulatedValue
  }
  return initialValue
}

export const handleUpdateCurrentTimeSimulation = (
  previousSimulatedValue,
  initialValue,
  activate,
  reset,
  timerMode
) => {
  if (reset) {
    return initialValue
  }
  if (activate) {
    return handleActivateValueUpdate(
      previousSimulatedValue,
      initialValue,
      timerMode
    )
  }
  return handleNonActivateNonResetValueUpdate(
    previousSimulatedValue,
    initialValue,
    timerMode
  )
}

export const handleLogicSetDelays = (
  currFinalGateSim,
  prevOutputtingProps,
  delays
) => {
  const prevFinalGateVal = getOr(
    false,
    'finalGateSimulation',
    prevOutputtingProps
  )
  const prevOutputtingValue = getOr(false, 'outputting', prevOutputtingProps)
  const persistentSignal = currFinalGateSim === prevFinalGateVal
  const relevantDelayValue = delays.get(
    currFinalGateSim ? 'delayActivate' : 'delayDeactivate'
  )
  const decrementedCountdown =
    getOr(relevantDelayValue, 'countdown', prevOutputtingProps) - 1
  if (
    relevantDelayValue === 0 || // don't need to delay
    (decrementedCountdown < 1 && persistentSignal) // we've already delayed
  ) {
    // Normalize null (all-empty logic set) to false so carry conditions and
    // output derivation never receive null as a live outputting value.
    // Gate simulation values (abGateSimulation etc.) stay null for the display layer.
    return Map({
      outputting: currFinalGateSim ?? false,
      countdown: 0,
    })
  }
  return Map({
    outputting: prevOutputtingValue,
    countdown: persistentSignal ? decrementedCountdown : relevantDelayValue,
  })
}

export const handleGetGateDependencySimulations = (
  conditions,
  accum,
  gatePrefix
) => {
  const dependencySource = gatePrefix === 'final' ? accum : conditions
  return gateDependencyKeys[gatePrefix].map((key) => dependencySource.get(key))
}

export const handleParentQueryEvaluation = (
  currentSimulatedValues,
  inProgressSimulatedEvaluations,
  query
) => {
  const queryUri = getQueryUri(query)
  const isTimer = getEq('queryType', TIMER, query)
  const conditions = query.get('conditions')
  // Use null for conditions that were never evaluated (empty/unfilled slots).
  // This lets getGateOpFnAndCompare treat them as "no input" rather than false.
  // AND gates require both inputs present; a null slot propagates null (not true).
  const conditionEvaluations = conditions.map((uri) =>
    inProgressSimulatedEvaluations.has(uri)
      ? getInOr(false, [uri, 'evaluation'], inProgressSimulatedEvaluations)
      : null
  )

  if (isTimer) {
    const initialValue = getInitialValue(query)
    const timerMode = getTimerMode(query)
    const activate = get(0, conditionEvaluations)
    const reset = get(1, conditionEvaluations)
    const previousSimulatedValue = getInOr(
      // if the previous timer value doesn't exist, we haven't simulated yet
      // so we pad the initial value by one, so the decrement below reflects
      // the accurate timer value
      initialValue + 1,
      [queryUri, 'current'],
      currentSimulatedValues
    )
    const current = handleUpdateCurrentTimeSimulation(
      previousSimulatedValue,
      initialValue,
      activate,
      reset,
      timerMode
    )

    return Map({
      [queryUri]: Map({
        current,
        outputting: activate,
      }),
    })
  }

  const gateSims = gates.reduce((accum, gatePrefix) => {
    const gateSimulationName = `${gatePrefix}${simSuffix}`
    const op = query.get(`${gatePrefix}${opSuffix}`)
    const upstreamSimEvals = handleGetGateDependencySimulations(
      conditionEvaluations,
      accum,
      gatePrefix
    )
    const previousGateSim = getInOr(
      false,
      [queryUri, gateSimulationName],
      currentSimulatedValues
    )
    const previousEnableSim = getPreviousEnableSimulation(
      currentSimulatedValues,
      query,
      gatePrefix
    )

    const baseGateSimulationProps = {
      op,
      previousGateSim,
      upstreamSimEvals,
      previousEnableSim,
    }

    const evaluation = getGateOpFnAndCompare(baseGateSimulationProps)
    return accum.set(gateSimulationName, evaluation)
  }, Map())

  const prevOutputtingProps = pickIn(
    ['finalGateSimulation', 'countdown', 'outputting'],
    [queryUri],
    currentSimulatedValues
  )

  const outputting = handleLogicSetDelays(
    gateSims.get('finalGateSimulation'),
    prevOutputtingProps,
    pick(['delayActivate', 'delayDeactivate'], query)
  )

  return Map({
    [queryUri]: gateSims.merge(outputting),
  })
}

// we want to ensure that we process the child conditions before taking care of the parent
export const pickAndOrderUris = ({ conditions = [], uri }) => [
  ...conditions,
  uri,
]

// morphs the feature entities to a an array of URIs from left to right,
// condition to query
// to go from right to left, add a `reverse` after the first flatten
export const reduceLogicNodeEntitiesToArrayOfUris = compose(
  flatten,
  map(pickAndOrderUris),
  flatten,
  values,
  safeToJS
)

export const createAllUrisArray = (
  orphanQueriesMap,
  functionGraphsMap,
  outputsByQueryUrisMap
) => {
  const orphanUris = reduceLogicNodeEntitiesToArrayOfUris(orphanQueriesMap)
  const functionUris = reduceLogicNodeEntitiesToArrayOfUris(functionGraphsMap)
  const outputQueryUris = outputsByQueryUrisMap.keySeq().toJS()
  const signalUris = Object.values(POWER_NET_LEVEL_SIGNAL_URIS)
  const fuseUris = outputsByQueryUrisMap
    .valueSeq()
    .map((list) => list.first())
    .filter(has('virtualFuseSetting'))
    .map(prop('id'))

  return [
    ...new Set([
      ...orphanUris,
      ...functionUris,
      ...outputQueryUris,
      ...signalUris,
      ...fuseUris,
    ]),
  ] // deduplicate any uris just in case
}

export const deriveSimulations = (
  currentSimulatedValues,
  allQueriesMap,
  allUris
) =>
  allUris.reduce((accumulator, queryUri) => {
    const fuseSimulationCurrent = currentSimulatedValues.getIn(
      [queryUri, 'currentAmps'],
      0
    )
    if (fuseSimulationCurrent > 0) {
      return accumulator.merge(
        new Map({ [queryUri]: currentSimulatedValues.get(queryUri) })
      )
    }

    const query = allQueriesMap.get(queryUri)
    // this is an empty query row, skip it!
    if (!query || isEmptyCondition(query)) {
      return accumulator
    }

    const queryType = query.get('queryType')

    if (queryType === COMPLEX || queryType === TIMER) {
      return accumulator.merge(
        handleParentQueryEvaluation(currentSimulatedValues, accumulator, query)
      )
    }

    return accumulator.merge(
      handleConditionQueryEvaluation(
        currentSimulatedValues,
        allQueriesMap,
        accumulator,
        query
      )
    )
  }, Map())

export const findAndResetTimeDependentQueries = (
  conditions = Map(),
  queries = Map()
) => {
  const resetConditions = conditions.reduce((accumulator, condition) => {
    if (conditionReferencesParent(condition)) {
      return accumulator.set(
        getQueryUri(condition),
        Map({
          evaluation: false,
          previousBaseResult: false,
          edgeEvaluation: null,
        })
      )
    }
    return accumulator
  }, Map())

  const resetQueries = queries.reduce((accumulator, query) => {
    const queryUri = getQueryUri(query)
    if (query.has('initialValue')) {
      return accumulator.set(
        queryUri,
        Map({
          current: getInitialValue(query),
        })
      )
    }

    const resetGateEvaluations = gates.reduce((accum, gatePrefix) => {
      if (query.get(`${gatePrefix}${opSuffix}`) === TOGGLE) {
        return accum.set(`${gatePrefix}${simSuffix}`, false)
      }
      return accum
    }, Map())

    if (resetGateEvaluations.size > 0) {
      return accumulator.set(
        queryUri,
        resetGateEvaluations.merge(Map({ countdown: 0 }))
      )
    }
    return accumulator.setIn([queryUri, 'countdown'], 0)
  }, Map())

  return resetConditions.merge(resetQueries)
}

export const determineOutputSimulationEvaluation = (
  outputQuerySimulationObj,
  output
) => {
  if (!outputQuerySimulationObj) {
    return false
  }

  const outputQuerySimValue = getSimulatedValue(outputQuerySimulationObj)
  const outputQueryHasCurrentProperty = has('current', outputQuerySimulationObj)
  const outputOperator = output.get('operator')
  const outputValue = output.get('value')

  // For timer outputs (which have a 'current' property), the output's numeric value is
  // compared against the countdown (e.g. countdown === 0 means timer complete).
  // For boolean condition outputs (no 'current' property), the output is simply active
  // when its driving condition evaluates to TRUE. The output's 'value' field in this
  // case is a hardware pin/CAN value (not a simulation trigger target), so we compare
  // against true rather than Boolean(outputValue) — which would invert the result for
  // any output where the API stores value: 0.
  if (!outputQueryHasCurrentProperty) {
    return Boolean(outputQuerySimValue)
  }

  return deriveEvaluationResult({
    simulatedValue: outputQuerySimValue,
    operator: outputOperator,
    value: outputValue,
  })
}

/**
 *
 * @param Immutable Map({ [queryUri || signalUri || canId]: simulationEvaluationObject, ... }) newSimulations
 * @param Immutable Map({ [outputQueryUri]: [{outputObj}, {outputObj}...]}) outputsByUris
 *
 * Takes a map of newly derived simulations and a map of output lists by query uri and returns a map of
 * output evaluations by outputId. The newly derived simulations
 *
 * @returns Immutable Map({
 *   [outputId]: simulationEvaluationObject,
 *   [outputId]: simulationEvaluationObject,
 *   ...
 * })
 */

export const deriveOutputSimulatedEvaluations = (
  newSimulations,
  outputsByUris,
  outputsByQueryUris
) => {
  // reduce over the immutable outputsByUris to change the shape of the returned object
  const outputSims = outputsByUris.reduce(
    (accumulator, immOutputList, outputQueryUri) => {
      // any outputs in the `outputList` will depend, somehow, on this outputQuerySimulation
      const outputQuerySimulation = newSimulations.get(outputQueryUri)

      // each output query has _at least_ one output, but could have multiple.
      const outputSimulationsMap = immOutputList.reduce((accum, immOutput) => {
        const outputId = immOutput.get('id')
        const outputSimulation = newSimulations.get(outputId)

        // outputs with an open fuse
        if (outputSimulation && outputSimulation.get('fuseOpen')) {
          return accum.merge(makeOutputtingSingleMap(outputId, false))
        }

        const sim = determineOutputSimulationEvaluation(
          outputQuerySimulation,
          immOutput,
          outputSimulation
        )
        return accum.merge(makeOutputtingSingleMap(outputId, sim))
      }, Map())

      return accumulator.merge(outputSimulationsMap)
    },
    Map()
  )

  return outputSims
}

const THREE_POSITION_SWITCH = 'three-position-switch'

export function handleDeriveRelatedInputValue(uri, value, signals) {
  // related inputs are only updated if the current update is going from
  // 'off' to 'on
  if (!value || value === 0) {
    return null
  }
  const relatedInputs = getIn([uri, 'relatedInputs'], signals)
  // we only need to update if the current signal has any related iputs
  if (!relatedInputs) {
    return null
  }

  const newValues = relatedInputs.reduce((accum, val, key) => {
    // currently 3 position switches are the only inputs that we
    // are controlling side effects for
    if (val !== THREE_POSITION_SWITCH) {
      return accum
    }

    // if we've made it this far, business rules dictacte that
    // all related inputs should be reset to their 'defaultValueOverride'
    const defaultValue = getIn(
      [uri, 'interface', 'defaultValueOverride'],
      signals
    )
    return [...accum, [key, defaultValue]]
  }, [])

  return newValues
}

// calculates PNL for simulation purposes
// given battery voltage, ignition status, and engine speed
// calculate the active power net levels
// PBI/57136

export const calculatePowerNetLevels = ({
  batteryVoltage,
  ignitionStatus,
  engineSpeed,
}) => {
  const { FIRST_GROUP, SECOND_GROUP, THIRD_GROUP, LAST_GROUP } =
    POWER_NET_LEVELS_MAPPING
  const ALL_GROUPS = [FIRST_GROUP, SECOND_GROUP, THIRD_GROUP, LAST_GROUP]

  const engineOn = engineSpeed > 0
  const isOff = ignitionStatus === 0
  const isRunning = ignitionStatus === 2
  const isCranking = ignitionStatus === 3

  if (engineOn) {
    // engine running
    // | ignition | low   | normal | high    |
    // | off      | all   | all    | all     |
    // | acc      | all   | all    | all     |
    // | run      | 5/3/0 | all    | 7/5/3/0 |
    // | crank    | all   | all    | 7/5/3/0 |
    const isLow = batteryVoltage < BATTERY_VOLTAGE_RANGE.LOW

    if (isLow && isRunning) {
      return [SECOND_GROUP, THIRD_GROUP, LAST_GROUP]
    }

    return ALL_GROUPS
  }

  // engine off
  // | ignition | low | normal | high    |
  // | off      | 0   | 3/0    | 7/5/3/0 |
  // | acc      | 0   | 5/3/0  | 7/5/3/0 |
  // | run      | 0   | 5/3/0  | 7/5/3/0 |
  // | crank    | all | all    | 7/5/3/0 |

  const { LOW_OFF, HIGH_OFF } = BATTERY_VOLTAGE_RANGE
  const isLow = batteryVoltage < LOW_OFF
  const isHigh = batteryVoltage > HIGH_OFF

  if (isHigh) return ALL_GROUPS

  if (isLow) {
    if (isCranking) {
      return ALL_GROUPS
    }
    return [LAST_GROUP]
  }

  if (isOff) {
    return [THIRD_GROUP, LAST_GROUP]
  }

  if (isCranking) {
    return ALL_GROUPS
  }

  return [SECOND_GROUP, THIRD_GROUP, LAST_GROUP]
}
